/**
 * Asking the subscribers to an issuance to pay for their shares.
 *
 * From the 14 Aug round's last bundle. It is the one place the cap table sends
 * something out, and it exists because the alternative is somebody writing
 * twelve emails by hand with twelve different amounts in them — which is how a
 * register and an inbox start to disagree.
 *
 * ## The numbers here are shown, not sent
 *
 * The amounts in this screen come from the same purchases the register draws
 * the cap table from, and they are recomputed on the server before anything is
 * delivered. The browser sends who to write to, never what they owe: see
 * `functions-webhook/src/sendPaymentRequest.ts`.
 *
 * ## Bilingual on purpose
 *
 * Every other string in this app is English by decision 1 of the same round.
 * These two are not app copy — they are the message an investor reads, and the
 * investors are Norwegian. Both templates are editable before sending; the
 * language buttons swap the starting text and nothing else.
 */
import { useMemo, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { fmtKrExact, fmtKrPrice } from '@/shared/util/money'
import { callFunction } from '@/shared/firebase/callFunction'
import { fmtDateAbs, formatDate, plusDaysIso } from '@/shared/util/dates'
import type { CompanyDetails } from '@/types/company'
import { effectivePrice } from '@/types/ownership'
import type { Issuance, Shareholder } from '@/types/ownership'

const nb = (n: number) => n.toLocaleString('nb-NO')

/** ISO date, `days` after the issuance's own date. */

const TEMPLATES = {
  no: {
    subject: 'Betaling for aksjer i {{emisjon}}',
    body: `Hei {{navn}},

Takk for at du tegnet deg i {{emisjon}}. Du har tegnet {{antall}} aksjer til {{kurs}} per aksje, som utgjør {{beløp}}.

Beløpet betales til konto {{konto}} innen {{frist}}. Merk betalingen med navnet ditt.

Aksjene registreres i aksjeeierboken når beløpet er mottatt. Si fra hvis noe ser feil ut, så retter vi det med en gang.

Vennlig hilsen
Skida`,
  },
  en: {
    subject: 'Payment for your shares in {{issuance}}',
    body: `Hi {{name}},

Thank you for subscribing in {{issuance}}. You have subscribed for {{shares}} shares at {{price}} per share, which comes to {{amount}}.

Please pay to account {{account}} by {{deadline}}, marking the payment with your name.

The shares are entered in the share register once the payment arrives. Tell us if anything looks wrong and we will correct it straight away.

Best,
Skida`,
  },
}

/** Norwegian in the Norwegian template, English in the English one — both work. */
const TAGS = {
  no: '{{navn}} {{antall}} {{kurs}} {{beløp}} {{spesifikasjon}} {{frist}} {{konto}} {{emisjon}} {{selskap}}',
  en: '{{name}} {{shares}} {{price}} {{amount}} {{breakdown}} {{deadline}} {{account}} {{issuance}} {{company}}',
}

interface Row {
  id: string
  name: string
  email: string
  shares: number
  amount: number
  /** One entry per price this holder pays in the round. See `priceLines`. */
  lines: { shares: number; price: number }[]
}

/**
 * What this holder pays per share, in words that survive more than one price.
 *
 * Emisjon 4 is the reason this is not a number: the round is kr 663, eight
 * holders took shares up at the kr 307,47 option strike, two signed at
 * kr 461,22 before it opened, and Atticus does both at once — 54 at the strike,
 * 36 at the round price. A single figure here would state one price beside an
 * amount computed from another, with a bank account underneath.
 *
 * Mirrored in `functions-webhook/src/sendPaymentRequest.ts`, which is the one
 * that actually sends. The two must agree; grep `priceLines`.
 */
function priceLines(lines: Row['lines']): string {
  if (lines.length === 0) return '—'
  if (lines.length === 1) return fmtKrPrice(lines[0]!.price)
  // "kr 307,47 x 54 + kr 663,00 x 36" — reads the same in both templates.
  return lines.map((l) => `${fmtKrPrice(l.price)} × ${nb(l.shares)}`).join(' + ')
}

/** The same split, one line each, for a template that wants it set out. */
function priceBreakdown(lines: Row['lines']): string {
  return lines
    .map((l) => `${nb(l.shares)} aksjer à ${fmtKrPrice(l.price)} = ${fmtKrExact(l.shares * l.price)}`)
    .join('\n')
}

export function PaymentRequestModal({
  issuance,
  holders,
  me,
  company,
  onClose,
}: {
  issuance: Issuance
  holders: Shareholder[]
  /** The signed-in address — where a test goes. */
  me: string
  /** For the default account and the company name in the body. */
  company: CompanyDetails
  onClose: () => void
}) {
  const [lang, setLang] = useState<'no' | 'en'>('no')
  const [subject, setSubject] = useState(TEMPLATES.no.subject)
  const [body, setBody] = useState(TEMPLATES.no.body)
  const [deadline, setDeadline] = useState(() => plusDaysIso(issuance.date, 14))
  // Defaults to the company account and says which one it is using, per
  // decision 31 — an account number typed fresh each send is one typo away from
  // sending investors' money somewhere else.
  const [account, setAccount] = useState(company.bankAccount)
  const [dropped, setDropped] = useState<string[]>([])
  const [busy, setBusy] = useState<'test' | 'live' | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tested, setTested] = useState('')

  /**
   * An open round is invoiced from its tegningsliste — R3.7, 24 Aug 2026.
   *
   * A round that has not closed has issued nothing, so it has no purchases and
   * reading them found nobody: this modal said "Nobody has subscribed for shares
   * in this round yet" over Emisjon 4 and its forty subscribers. A payment
   * request drafted against the subscription list before closing is the actual
   * use — the money is what closes the round.
   *
   * Only one of the two ledgers is ever read, never both: adding them would
   * invoice a holder twice on the day a subscription becomes a purchase.
   * `sendPaymentRequest.ts` branches on the same field, because this preview
   * must not disagree with the email it is a preview of.
   */
  const ongoing = issuance.status === 'ongoing'

  /** Who subscribed in this round, and for how much. */
  const rows = useMemo((): Row[] => {
    return holders
      .map((h) => {
        const ledger: { issuanceId: string; shares: number; pricePerShare?: number | null }[] =
          ongoing ? (h.subscriptions ?? []) : h.purchases
        const mine = ledger.filter((p) => p.issuanceId === issuance.id)
        const shares = mine.reduce((n, p) => n + (p.shares || 0), 0)
        // Summed per purchase, because a round can carry several prices — the
        // round's, an agreement signed before it opened, an option strike. The
        // server computes it the same way; this preview must not disagree with
        // the email it is a preview of.
        const lines: Row['lines'] = []
        for (const p of mine) {
          const n = p.shares || 0
          if (n <= 0) continue
          const price = effectivePrice(p, issuance) ?? 0
          // Two rows at the same price are one price to the person reading it.
          const same = lines.find((l) => l.price === price)
          if (same) same.shares += n
          else lines.push({ shares: n, price })
        }
        lines.sort((a, b) => a.price - b.price)
        const amount = lines.reduce((n, l) => n + l.shares * l.price, 0)
        return { id: h.id, name: h.name, email: h.email, shares, amount, lines }
      })
      .filter((r) => r.shares > 0)
      .sort((a, b) => b.amount - a.amount)
  }, [holders, issuance, ongoing])

  const included = rows.filter((r) => !dropped.includes(r.id) && r.email)
  const missing = rows.filter((r) => !r.email)
  const totalDue = included.reduce((n, r) => n + r.amount, 0)

  const pickLang = (l: 'no' | 'en') => {
    setLang(l)
    setSubject(TEMPLATES[l].subject)
    setBody(TEMPLATES[l].body)
  }

  /** The same substitution the server makes, for the preview only. */
  const merge = (t: string, r: Row) =>
    t.replace(/\{\{([A-Za-zÆØÅæøå0-9_]+)\}\}/g, (whole, key: string) => {
      const values: Record<string, string> = {
        name: r.name,
        navn: r.name,
        shares: nb(r.shares),
        antall: nb(r.shares),
        amount: fmtKrExact(r.amount),
        'beløp': fmtKrExact(r.amount),
        // This holder's own price, not the round's — the round's is only the
        // fallback inside `effectivePrice` for a row that carries none.
        price: priceLines(r.lines),
        kurs: priceLines(r.lines),
        breakdown: priceBreakdown(r.lines),
        spesifikasjon: priceBreakdown(r.lines),
        issuance: issuance.name,
        emisjon: issuance.name,
        deadline: deadline ? fmtDateAbs(deadline) : '',
        frist: deadline ? fmtDateAbs(deadline) : '',
        account,
        konto: account,
        company: company.legalName,
        selskap: company.legalName,
      }
      return key in values ? values[key]! : whole
    })

  const sample = included[0] ?? rows[0] ?? null
  /** What a test proves is only true while the text is unchanged. */
  const fingerprint = `${lang}|${subject}|${body}|${deadline}|${account}`

  const send = async (mode: 'test' | 'live') => {
    setBusy(mode)
    setError(null)
    setNote(null)
    try {
      const out = await callFunction<{
        sent?: number
        failed?: number
        ok?: boolean
        skipped?: { noEmail: number; suppressed: number }
      }>('crmSendPaymentRequest', {
        method: 'POST',
        body: {
          issuanceId: issuance.id,
          mode,
          testTo: me,
          subject,
          body,
          language: lang,
          deadline,
          account,
          shareholderIds: included.map((r) => r.id),
        },
      })
      if (mode === 'test') {
        setTested(fingerprint)
        setNote(`Test sent to ${me}.`)
      } else {
        const skipped =
          (out.skipped?.noEmail ?? 0) + (out.skipped?.suppressed ?? 0) > 0
            ? ` ${out.skipped?.noEmail ?? 0} without an address, ${out.skipped?.suppressed ?? 0} suppressed.`
            : ''
        setNote(`Sent to ${out.sent ?? 0}${out.failed ? `, ${out.failed} failed` : ''}.${skipped}`)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The send failed.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal
      title={`Payment request — ${issuance.name}`}
      onClose={onClose}
      width={900}
      footer={
        <>
          <span className="payreq__count">
            {included.length} recipient{included.length === 1 ? '' : 's'} · kr {nb(Math.round(totalDue))} due
          </span>
          <button
            className="btn btn--ghost"
            disabled={busy !== null}
            onClick={() => void send('test')}
          >
            {busy === 'test' ? 'Sending…' : 'Send test to me'}
          </button>
          <button
            className="btn btn--primary"
            disabled={busy !== null || included.length === 0}
            onClick={() => void send('live')}
          >
            {busy === 'live' ? 'Sending…' : `Send to ${included.length}`}
          </button>
        </>
      }
    >
      <div className="payreq">
        <p className="recform__note payreq__lede">
          To everyone who subscribed for shares in this issuance. Share count, price and amount due
          are merged per recipient — {issuance.date ? formatDate(issuance.date) : 'undated'} ·{' '}
          {issuance.pricePerShare === null ? 'no price set' : `${fmtKrPrice(issuance.pricePerShare)} per share`}.
        </p>

        {/* Which ledger this is drawn from, said out loud. The amounts below
            look identical either way, and a request built from a tegningsliste
            is a different document from one built from settled purchases — the
            shares do not exist yet, and the money is what makes them. */}
        {ongoing && (
          <p className="payreq__provisional">
            <strong>{issuance.name} has not closed.</strong> These are subscriptions, not settled
            purchases: the shares do not exist yet and count towards nobody&rsquo;s holding. The
            amounts are what each subscriber has signed for, at the price agreed with them, and the
            round closes when the money arrives.
          </p>
        )}

        <div className="payreq__cols">
          <div className="payreq__compose">
            <div className="recform__grid">
              <label className="field">
                <span>Payment deadline</span>
                <input
                  className="input"
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                />
              </label>
              <label className="field">
                <span>Account</span>
                <input
                  className="input"
                  value={account}
                  placeholder="1234.56.78901"
                  onChange={(e) => setAccount(e.target.value)}
                />
              </label>
            </div>

            <div className="payreq__lang">
              <span className="card-label">Language</span>
              <button className="chip" aria-pressed={lang === 'no'} onClick={() => pickLang('no')}>
                Norsk
              </button>
              <button className="chip" aria-pressed={lang === 'en'} onClick={() => pickLang('en')}>
                English
              </button>
            </div>

            <label className="field">
              <span>Subject</span>
              <input
                className="input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Body</span>
              <textarea
                className="input payreq__body"
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
            <p className="micro">Merge fields: <code>{TAGS[lang]}</code></p>
            <p className="micro">
              {account === company.bankAccount && company.bankAccount
                ? 'Using the account from Admin → Company details.'
                : account
                  ? 'Using an account typed for this send only.'
                  : 'No account yet — set one in Admin → Company details, or type one here.'}
            </p>
          </div>

          <div className="payreq__side">
            <div className="payreq__list">
              <div className="payreq__listhead">
                <span className="card-label">Subscribers</span>
                <span className="micro">{included.length} selected</span>
              </div>
              {rows.map((r) => {
                const off = dropped.includes(r.id) || !r.email
                return (
                  <label className={`payreq__row${off ? ' payreq__row--off' : ''}`} key={r.id}>
                    <input
                      type="checkbox"
                      checked={!dropped.includes(r.id) && Boolean(r.email)}
                      disabled={!r.email}
                      onChange={() =>
                        setDropped((d) =>
                          d.includes(r.id) ? d.filter((x) => x !== r.id) : [...d, r.id],
                        )
                      }
                    />
                    <span className="payreq__who">
                      <strong>{r.name}</strong>
                      <span className={r.email ? 'micro' : 'payreq__noaddr'}>
                        {r.email || 'No email address on the record'}
                      </span>
                    </span>
                    <span className="payreq__amt">
                      kr {nb(Math.round(r.amount))}
                      {/* The price is on the row because it is no longer one
                          number for the round: an option strike, an agreement
                          and the round's price all appear in this list. */}
                      <span className="micro">
                        {nb(r.shares)} shares · {priceLines(r.lines)}
                      </span>
                    </span>
                  </label>
                )
              })}
              {rows.length === 0 && (
                <p className="empty-note">
                  {ongoing
                    ? 'Nothing on the tegningsliste for this round yet. It fills as subscriptions are recorded against it, and this list reads them rather than the purchases an open round has not made.'
                    : 'Nobody holds shares from this round, so there is nobody to invoice. Add the purchases to the round first.'}
                </p>
              )}
            </div>

            {missing.length > 0 && (
              <p className="recform__warn">
                {missing.length} subscriber{missing.length === 1 ? '' : 's'} without an email
                address {missing.length === 1 ? 'is' : 'are'} left out — add one on the record and
                they can be included.
              </p>
            )}

            {sample && (
              <div className="payreq__preview">
                <div className="card-label">Preview — {sample.name}</div>
                <div className="payreq__previewsub">{merge(subject, sample)}</div>
                <div className="payreq__previewbody">{merge(body, sample)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Not a gate — this is a dozen named investors and the merged body is
            on the screen. It says its piece and gets out of the way. */}
        {tested !== fingerprint && included.length > 0 && (
          <p className="recform__note">
            {tested
              ? 'The text has changed since the last test.'
              : 'No test sent of this text yet.'}
          </p>
        )}
        {note && <p className="recform__note payreq__done">{note}</p>}
        {error && <p className="recform__error">{error}</p>}
      </div>
    </Modal>
  )
}
